import React, { useState, useEffect } from "react";
import client, { apiErr } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button, Input, Card } from "../components/ui";
import { PenLine, ExternalLink, ShieldCheck, Loader2 } from "lucide-react";

const FONTS = [
  { key: "great_vibes", label: "Great Vibes", css: "'Great Vibes', cursive" },
  { key: "allura", label: "Allura", css: "'Allura', cursive" },
  { key: "sacramento", label: "Sacramento", css: "'Sacramento', cursive" },
  { key: "pacifico", label: "Pacifico", css: "'Pacifico', cursive" },
  { key: "plain", label: "Plain (block letters)", css: "inherit" },
];

const POLICIES = [
  ["accept_backup", "Backup & Recovery Policy", "/backup"],
  ["accept_cookies", "Cookies Policy", "/cookies"],
  ["accept_privacy", "Privacy Policy", "/privacy"],
  ["accept_terms", "Terms of Use", "/terms"],
];

export default function SignAgreement() {
  const { refreshUser, logout, lang, user } = useApp();
  const [platform, setPlatform] = useState("saas");
  const [name, setName] = useState(user?.name && user.name !== "User" ? user.name : "");
  const [font, setFont] = useState("great_vibes");
  const [acc, setAcc] = useState({ accept_backup: false, accept_cookies: false, accept_privacy: false, accept_terms: false });
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (document.getElementById("sig-fonts")) return;
    const l = document.createElement("link");
    l.id = "sig-fonts"; l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Allura&family=Great+Vibes&family=Pacifico&family=Sacramento&display=swap";
    document.head.appendChild(l);
  }, []);

  useEffect(() => {
    const which = platform === "native" ? "eula" : "saas_agreement";
    client.get("/public/legal", { params: { doc: which, lang: lang || "ENG" } })
      .then((r) => setDoc(r.data)).catch(() => setDoc(false));
  }, [platform, lang]);

  const allAccepted = Object.values(acc).every(Boolean);
  const canSign = platform === "saas" && name.trim().length >= 2 && allAccepted;
  const fontCss = (FONTS.find((f) => f.key === font) || {}).css || "inherit";

  const submit = async () => {
    setErr("");
    if (!canSign) return setErr("Type your name, choose a signature style and accept all four policies.");
    setBusy(true);
    try {
      await client.post("/agreements/sign", {
        platform, signature_name: name.trim(), signature_font: font, lang: lang || "ENG", ...acc,
      });
      await refreshUser();
    } catch (e) { setErr(apiErr(e)); }
    setBusy(false);
  };

  const openLanguageCopy = () => {
    if (!doc || !doc.content) return;
    const w = window.open("", "_blank");
    if (!w) return;
    const safe = String(doc.content).replace(/&/g, "&amp;").replace(/</g, "&lt;");
    w.document.write(
      `<!doctype html><html lang="${lang || "en"}"><head><meta charset="utf-8"><title>${doc.title || "Agreement"}</title>` +
      `<style>body{font-family:system-ui,Segoe UI,Arial,sans-serif;max-width:820px;margin:32px auto;padding:0 20px;line-height:1.55;color:#111}` +
      `h1{color:#0F62FE;font-size:22px;margin-bottom:4px} .banner{background:#eff6ff;border:1px solid #0F62FE;border-radius:8px;padding:10px 14px;font-size:13px;margin:14px 0}` +
      `pre{white-space:pre-wrap;font-family:inherit;font-size:14px}</style></head><body>` +
      `<h1>${doc.title || "Agreement"}</h1>` +
      `<div class="banner">Reference copy in your language for your records. The official, legally-binding document you e-sign is the <b>English</b> SaaS Agreement.</div>` +
      `<pre>${safe}</pre>` +
      `<script>setTimeout(function(){window.print();},450);<\/script></body></html>`
    );
    w.document.close();
  };

  return (
    <div className="min-h-screen overflow-auto p-4 sm:p-8" style={{ background: "var(--app-bg)" }}>
      <div className="mx-auto max-w-3xl space-y-5">
        <Card className="space-y-2" data-testid="sign-agreement">
          <div className="flex items-center gap-2 font-head text-2xl"><PenLine size={24} className="text-[var(--primary)]" /> Sign your agreement</div>
          <p className="text-sm opacity-70">Before you can use the applications, please choose your platform, read and accept the policies, and e-sign the agreement. Your signed copy will be emailed to you. The date you sign becomes your annual Service-Charge anniversary.</p>
        </Card>

        <Card className="space-y-3">
          <div className="text-sm font-medium">1. Choose your platform</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <button type="button" onClick={() => setPlatform("saas")} data-testid="platform-saas"
              className={`rounded-lg border p-3 text-start transition-colors ${platform === "saas" ? "border-[var(--primary)] bg-[var(--primary)]/8" : "border-black/15 dark:border-white/15"}`}>
              <div className="font-medium">SaaS (web) edition</div>
              <div className="text-xs opacity-60">Hosted web applications. You sign the SaaS Agreement.</div>
            </button>
            <button type="button" disabled data-testid="platform-native"
              className="cursor-not-allowed rounded-lg border border-dashed border-black/15 p-3 text-start opacity-60 dark:border-white/15">
              <div className="font-medium">Native app edition <span className="ms-1 rounded-full bg-black/10 px-2 py-0.5 text-[10px] dark:bg-white/10">Unavailable</span></div>
              <div className="text-xs opacity-60">Downloadable desktop/mobile apps (EULA). Coming soon — premium pricing.</div>
            </button>
          </div>
        </Card>

        <Card className="space-y-2">
          <div className="text-sm font-medium">2. Read the {doc?.title || (platform === "native" ? "EULA" : "SaaS Agreement")}</div>
          <div className="max-h-56 overflow-auto rounded-md border border-black/10 p-3 text-xs leading-relaxed opacity-90 dark:border-white/10 whitespace-pre-line" data-testid="agreement-text">
            {doc === false ? "Unable to load the agreement text." : doc ? doc.content : "Loading…"}
          </div>
          <div className="rounded-md border border-[var(--primary)]/30 bg-[var(--primary)]/5 p-3 text-xs" data-testid="lang-copy-prompt">
            <p className="opacity-80">📄 Please take a copy in your language for your records. The document you e-sign is the official <b>English</b> SaaS Agreement; the version shown above is a reference translation in your language.</p>
            <Button variant="ghost" className="mt-2" onClick={openLanguageCopy} disabled={!doc || !doc.content} data-testid="download-lang-copy">
              Open / save my-language copy
            </Button>
          </div>
        </Card>

        <Card className="space-y-3">
          <div className="text-sm font-medium">3. Confirm you have read and accept all four policies</div>
          {POLICIES.map(([k, label, href]) => (
            <label key={k} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={acc[k]} onChange={(e) => setAcc({ ...acc, [k]: e.target.checked })} data-testid={`chk-${k}`} />
              <span>I have read and accept the</span>
              <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-medium text-[var(--primary)] hover:underline" data-testid={`link-${k}`}>{label} <ExternalLink size={12} /></a>
            </label>
          ))}
        </Card>

        <Card className="space-y-3">
          <div className="text-sm font-medium">4. E-sign</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input label="Type your full legal name" value={name} onChange={(e) => setName(e.target.value)} data-testid="sig-name" />
            <label className="block text-sm"><span className="opacity-70">Signature style</span>
              <select value={font} onChange={(e) => setFont(e.target.value)} data-testid="sig-font"
                className="mt-1 w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm">
                {FONTS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
            </label>
          </div>
          <div className="rounded-md border border-black/10 bg-white/50 p-4 dark:border-white/10 dark:bg-black/20">
            <div className="text-[10px] uppercase tracking-wide opacity-50">Signature preview</div>
            <div data-testid="sig-preview" style={{ fontFamily: fontCss, fontSize: font === "plain" ? "20px" : "34px", lineHeight: 1.4, minHeight: "40px" }}>
              {name || "Your signature"}
            </div>
          </div>
          {err && <div className="text-sm text-[#DA1E28]" data-testid="sign-error">{err}</div>}
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={logout}>Sign out</Button>
            <Button onClick={submit} disabled={busy || !canSign} data-testid="sign-submit">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} Sign &amp; accept
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
